export type CartLineInput = {
  productId: string
  productSlug: string
  name: string
  imageUrl: string | null
  variantId: string
  variantLabel: string
  wrapperId: string | null
  wrapperLabel: string | null
  addonIds: string[]
  addonLabels: string[]
  unitPrice: number
  /** Full line MRP (stem compare-at plus wrapper and add-ons). Null when there is no discount. */
  compareAtPrice?: number | null
  /** Product can use express delivery. Standard products are false. */
  expressEligible?: boolean
  quantity?: number
}

export type CartLine = CartLineInput & {
  id: string
  quantity: number
}

export function buildCartLineId(input: {
  productId: string
  variantId: string
  wrapperId: string | null
  addonIds: string[]
}): string {
  const addons = [...input.addonIds].sort().join(',')
  return [input.productId, input.variantId, input.wrapperId ?? '', addons].join('::')
}

export function cartLineCompareAt(
  compareAt: number | null,
  basePrice: number,
  extras: number,
): number | null {
  if (compareAt == null || compareAt <= basePrice) return null
  return compareAt + extras
}

export function createCartLine(input: CartLineInput): CartLine {
  const quantity = Math.max(1, Math.floor(input.quantity ?? 1))
  const compareAtPrice =
    input.compareAtPrice != null && input.compareAtPrice > input.unitPrice
      ? input.compareAtPrice
      : null
  return {
    ...input,
    addonIds: [...input.addonIds].sort(),
    compareAtPrice,
    id: buildCartLineId(input),
    quantity,
  }
}

export function addCartLine(lines: CartLine[], input: CartLineInput): CartLine[] {
  const next = createCartLine(input)
  const existing = lines.find((line) => line.id === next.id)
  if (!existing) return [...lines, next]
  return lines.map((line) =>
    line.id === next.id
      ? {
          ...line,
          quantity: line.quantity + next.quantity,
          unitPrice: next.unitPrice,
          compareAtPrice: next.compareAtPrice,
          expressEligible: next.expressEligible,
        }
      : line,
  )
}

export function updateCartLineQuantity(
  lines: CartLine[],
  lineId: string,
  quantity: number,
): CartLine[] {
  const nextQty = Math.floor(quantity)
  if (nextQty <= 0) return lines.filter((line) => line.id !== lineId)
  return lines.map((line) =>
    line.id === lineId ? { ...line, quantity: nextQty } : line,
  )
}

export function removeCartLine(lines: CartLine[], lineId: string): CartLine[] {
  return lines.filter((line) => line.id !== lineId)
}

export function cartItemCount(lines: CartLine[]): number {
  return lines.reduce((sum, line) => sum + line.quantity, 0)
}

export function cartSubtotal(lines: CartLine[]): number {
  return lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0)
}

export function cartLineWasTotal(line: CartLine): number | null {
  if (line.compareAtPrice == null || line.compareAtPrice <= line.unitPrice) return null
  return line.compareAtPrice * line.quantity
}

/** MRP across the cart. Undiscounted lines count at their selling price. */
export function cartCompareAtTotal(lines: CartLine[]): number | null {
  if (!lines.some((line) => cartLineWasTotal(line) != null)) return null
  return lines.reduce((sum, line) => {
    const was = cartLineWasTotal(line)
    return sum + (was ?? line.unitPrice * line.quantity)
  }, 0)
}

export function cartSavings(lines: CartLine[]): number {
  const compareAt = cartCompareAtTotal(lines)
  if (compareAt == null) return 0
  return compareAt - cartSubtotal(lines)
}

export function cartQuantityForProduct(lines: CartLine[], productId: string): number {
  return lines
    .filter((line) => line.productId === productId)
    .reduce((sum, line) => sum + line.quantity, 0)
}

export type CartAddonInput = {
  id: string
  label: string
  price: number
}

export function addAddonToCartLine(
  lines: CartLine[],
  lineId: string,
  addon: CartAddonInput,
): CartLine[] {
  const line = lines.find((item) => item.id === lineId)
  if (!line || line.addonIds.includes(addon.id)) return lines
  const next = createCartLine({
    ...line,
    addonIds: [...line.addonIds, addon.id],
    addonLabels: [...line.addonLabels, addon.label],
    unitPrice: line.unitPrice + addon.price,
    compareAtPrice:
      line.compareAtPrice != null ? line.compareAtPrice + addon.price : null,
    quantity: line.quantity,
  })
  const without = lines.filter((item) => item.id !== lineId)
  const existing = without.find((item) => item.id === next.id)
  if (!existing) return [...without, next]
  return without.map((item) =>
    item.id === next.id
      ? {
          ...item,
          quantity: item.quantity + next.quantity,
          unitPrice: next.unitPrice,
          compareAtPrice: next.compareAtPrice,
        }
      : item,
  )
}

export function findCartLine(
  lines: CartLine[],
  input: {
    productId: string
    variantId: string
    wrapperId: string | null
    addonIds: string[]
  },
): CartLine | undefined {
  const id = buildCartLineId(input)
  return lines.find((line) => line.id === id)
}

export function parseCartLines(raw: string | null): CartLine[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item) => normalizeCartLine(item))
      .filter((item): item is CartLine => item != null)
  } catch {
    return []
  }
}

export function serializeCartLines(lines: CartLine[]): string {
  return JSON.stringify(lines)
}

function normalizeCartLine(value: unknown): CartLine | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Partial<CartLine>
  if (
    typeof row.productId !== 'string' ||
    typeof row.productSlug !== 'string' ||
    typeof row.name !== 'string' ||
    typeof row.variantId !== 'string' ||
    typeof row.variantLabel !== 'string' ||
    typeof row.unitPrice !== 'number' ||
    typeof row.quantity !== 'number'
  ) {
    return null
  }

  const addonIds = Array.isArray(row.addonIds)
    ? row.addonIds.filter((id): id is string => typeof id === 'string')
    : []
  const addonLabels = Array.isArray(row.addonLabels)
    ? row.addonLabels.filter((label): label is string => typeof label === 'string')
    : []

  return createCartLine({
    productId: row.productId,
    productSlug: row.productSlug,
    name: row.name,
    imageUrl: typeof row.imageUrl === 'string' ? row.imageUrl : null,
    variantId: row.variantId,
    variantLabel: row.variantLabel,
    wrapperId: typeof row.wrapperId === 'string' ? row.wrapperId : null,
    wrapperLabel: typeof row.wrapperLabel === 'string' ? row.wrapperLabel : null,
    addonIds,
    addonLabels,
    unitPrice: row.unitPrice,
    compareAtPrice: typeof row.compareAtPrice === 'number' ? row.compareAtPrice : null,
    expressEligible: typeof row.expressEligible === 'boolean' ? row.expressEligible : undefined,
    quantity: row.quantity,
  })
}
